/**
 * @fileoverview Backward-compat wrapper around the unified
 * `FilePreviewDialog`.
 *
 * The original implementation (header / footer / per-MIME renderer
 * dispatch with auth-aware blob materialization) was ~440 lines
 * duplicated across the other preview surfaces. That logic now lives in
 * `components/file-preview/` — this file builds the chat-scoped or
 * sandbox-scoped server URL, hands it to a {@link urlSource} with
 * `authRequired: true`, and forwards to the unified dialog.
 *
 * Kept as a thin wrapper (rather than deleted outright) so the
 * `SimpleAttachmentList` caller in this folder doesn't have to change.
 */
import { useMemo } from "react";

import {
  FilePreviewDialog as UnifiedFilePreviewDialog,
  urlSource,
} from "@/components/file-preview";
import { getChatIdFromPathname } from "@/lib/chat-icon";
import { getAuthHeader } from "@/utils";

interface FilePreviewDialogProps {
  /** Filename / path to preview. May include directory components. */
  filename: string;
  /** Sandbox ID for the legacy sandbox-keyed file URL fallback. */
  sandboxId?: string;
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback when the dialog should close. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Base URL prefix for API requests. Empty string in dev (vite proxy
 * handles relative paths); set from `VITE_API_BASE_URL` in production.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

/**
 * @deprecated Build a {@link urlSource} with `authRequired: true`
 * directly and use the unified `FilePreviewDialog`. This wrapper
 * exists only to keep the existing `attachment-list` caller working.
 */
export function FilePreviewDialog({
  filename,
  sandboxId,
  open,
  onOpenChange,
}: FilePreviewDialogProps) {
  /**
   * Resolve files by chat-scoped URL when we're inside a chat route. The
   * legacy sandboxId-keyed URL only works for chats whose
   * `sandbox_sessions` row still points at a live workspace dir; for
   * older chats it 404s even though the files are still recoverable
   * from the sync manifest. Falls back to sandboxId when chatId isn't
   * in the URL (bubble / standalone preview).
   *
   * Read directly from `window.location.pathname` instead of
   * `useParams` — chat pages replace the URL via
   * `window.history.replaceState` after the first stream, which leaves
   * React Router's match (and useParams) stuck on the pre-stream id.
   */
  const chatIdFromUrl = getChatIdFromPathname();

  const cleanFilename = filename.includes("/")
    ? filename.split("/").pop() || filename
    : filename;

  const source = useMemo(() => {
    const params = new URLSearchParams({ path: filename });
    const url = chatIdFromUrl
      ? `${API_BASE_URL}/workspace-files/chat/${chatIdFromUrl}/files/content?${params}`
      : `${API_BASE_URL}/workspace-files/${sandboxId}/files/content?${params}`;

    /**
     * Open-in-new-tab / download go through `window.open`, which can't send
     * the bearer header the (now auth-protected) content route requires. Mint
     * a short-lived single-file token and append `?token=` so the new tab
     * authenticates. Only the chat-scoped route exposes a content-token
     * endpoint; the legacy sandbox fallback returns the raw URL.
     */
    const resolveTokenizedUrl = chatIdFromUrl
      ? async () => {
          const authHeader = getAuthHeader();
          const res = await fetch(
            `${API_BASE_URL}/workspace-files/chat/${chatIdFromUrl}/files/content-token?${params}`,
            { headers: authHeader ? { Authorization: authHeader } : {} },
          );
          if (!res.ok) return url;
          const json = (await res.json()) as { data?: { token?: string } };
          const token = json?.data?.token;
          return token ? `${url}&token=${encodeURIComponent(token)}` : url;
        }
      : undefined;

    return urlSource({
      url,
      filename: cleanFilename,
      authRequired: true,
      resolveTokenizedUrl,
    });
  }, [filename, cleanFilename, chatIdFromUrl, sandboxId]);

  return (
    <UnifiedFilePreviewDialog
      source={source}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}
